import { FC } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Tag } from "@/assets/icons/ds";
import { getFriendsWithBenefitsRates } from "@/components/FriendsWithBenefits/FriendsWithBenefitsRates.tsx";
import { Pill } from "@/components/Interface/Pill/Pill.tsx";

interface Props {
  tribeDiscount: number;
}

/**
 * Next-steps teaser for the FWB welcome screen: discount pill (when applicable),
 * the three steps to join, and a price hint for the cheapest rate.
 */
export const WelcomeDiscountTeaser: FC<Props> = ({ tribeDiscount }) => {
  const { t } = useTranslation();
  const [cheapestRate] = getFriendsWithBenefitsRates({ t, tribeDiscount });
  const price = cheapestRate.discountPriceValue.toFixed(2);

  return (
    <div className="flex flex-col gap-3 text-foreground-primary">
      {tribeDiscount > 0 && (
        <Pill
          className="self-start bg-surface-accent-orange"
          text={t("friendsWithBenefits.welcome.discountShort", {
            discount: tribeDiscount,
          })}
          icon={<Tag className="size-6" />}
          size="lg"
        />
      )}

      <h2 className="text-headline-md font-semibold leading-sm tracking-[-0.32px]">
        {t("friendsWithBenefits.welcome.nextSteps")}
      </h2>

      <ol className="flex list-inside list-decimal flex-col gap-3 text-body-base leading-md tracking-[0.32px]">
        <li>{t("friendsWithBenefits.welcome.steps.register")}</li>
        <li>{t("friendsWithBenefits.welcome.steps.order")}</li>
        <li>{t("friendsWithBenefits.welcome.steps.benefit")}</li>
      </ol>

      <p className="text-body-base leading-none tracking-[0.32px]">
        <Trans
          i18nKey="friendsWithBenefits.welcome.priceHint"
          values={{ price }}
        />
      </p>
    </div>
  );
};
